import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Download, KeyRound, Loader2, Mail, Share, Smartphone, ThermometerSnowflake, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../utils/api';
import { setAuth } from '../utils/auth';
import { usePWAInstall } from '../hooks/use-pwa-install';

const modes = [
  { id: 'admin', label: 'Admin / Support', hint: 'Provision tenants, manage hardware, view cross-tenant alerts.', demoEmail: 'admin@coldchain.com', demoPassword: 'admin123' },
  { id: 'tenant', label: 'Tenant access', hint: 'Open compliance dashboard, alerts, audit binder, and tickets.', demoEmail: 'admin@currypalace.com', demoPassword: 'demo123' },
];

export const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const pwa = usePWAInstall();
  const [showInstallBanner, setShowInstallBanner] = useState(true);
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' | 'code'
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const activeMode = modes.find(m => m.id === mode);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      setAuth(response.data.token, response.data.user);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  const handleRequestCode = async () => {
    if (!email) { toast.error('Enter your email first'); return; }
    setLoading(true);
    try {
      await api.post('/auth/request-code', { email });
      setCodeSent(true);
      toast.success('Login code sent to your email');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/verify-code', { email, code });
      setAuth(r.data.token, r.data.user);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
    setLoading(false);
  };

  const fillDemo = (demoEmail, demoPassword) => { setEmail(demoEmail); setPassword(demoPassword); setShowDemo(false); };

  return (
    <div className="flex min-h-[100dvh] flex-col-reverse lg:flex-row" data-testid="login-page">
      {/* Brand panel — hidden on mobile */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-slate-950 px-8 py-10 lg:w-[55%] lg:px-14 lg:py-14">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="pointer-events-none absolute left-0 top-0 h-[600px] w-[600px] -translate-x-1/3 -translate-y-1/3 rounded-full bg-sky-500/8 blur-[140px]" />
        <div className="relative z-10 flex items-center gap-3" data-testid="login-brand-lockup">
          <div className="rounded-2xl bg-sky-500/10 p-3 ring-1 ring-sky-500/20"><ThermometerSnowflake className="h-5 w-5 text-sky-400" /></div>
          <div><p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500">ColdChain</p><p className="text-sm font-semibold text-white">Sentinel</p></div>
        </div>
        <div className="relative z-10 my-10 lg:my-0" data-testid="login-hero-panel">
          <h1 className="max-w-lg text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl" data-testid="login-heading">Food safety monitoring for multi-tenant operations.</h1>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400" data-testid="login-subheading">Sensors, alerts, HACCP logs, and compliance — all in one place.</p>
          <div className="mt-8 space-y-4">
            {[
              { label: 'Live temperature monitoring', detail: 'Real-time LoRaWAN sensor readings across all locations' },
              { label: 'HACCP compliance tools', detail: 'Audit binder, CCP tracking, and CSV export' },
              { label: 'Multi-tenant operations', detail: 'Full tenant isolation, admin impersonation, context switching' },
              { label: 'Full device traceability', detail: 'Device EUI, gateway, location, and equipment linked per reading' },
            ].map(item => (
              <div key={item.label} className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-400" />
                <div><p className="text-sm font-medium text-white">{item.label}</p><p className="mt-0.5 text-xs text-slate-500">{item.detail}</p></div>
              </div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-xs text-slate-700">Enterprise IoT compliance</p>
      </div>

      {/* Login form */}
      <div className="flex flex-1 flex-col justify-center bg-white px-5 py-6 sm:px-10 sm:py-10 lg:px-14" data-testid="login-form-panel">
        <div className="mx-auto w-full max-w-sm">
          {/* Mobile brand */}
          <div className="mb-5 flex items-center gap-3 lg:hidden">
            <div className="rounded-xl bg-slate-950 p-2.5 text-white"><ThermometerSnowflake className="h-4 w-4" /></div>
            <div><p className="text-sm font-semibold text-slate-950">ColdChain Sentinel</p><p className="text-[10px] text-slate-400">Enterprise IoT monitoring</p></div>
          </div>

          {/* PWA Install Banner — prominent on mobile, auto-shown */}
          {showInstallBanner && pwa.canInstall && (
            <div className="mb-5 overflow-hidden rounded-2xl border border-blue-200 bg-blue-50" data-testid="pwa-install-banner">
              <div className="flex items-start gap-3 p-4">
                <div className="rounded-xl bg-blue-600 p-2.5 text-white"><Smartphone className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-blue-900">Install ColdChain Sentinel</p>
                  <p className="mt-0.5 text-xs text-blue-700">Get instant alerts and monitor sensors from your home screen.</p>
                </div>
                <button type="button" onClick={() => setShowInstallBanner(false)} className="shrink-0 rounded-lg p-1 text-blue-400 hover:bg-blue-100"><X className="h-4 w-4" /></button>
              </div>
              {pwa.canNativeInstall ? (
                <button type="button" onClick={pwa.install} className="flex w-full items-center justify-center gap-2 border-t border-blue-200 bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700" data-testid="pwa-install-btn">
                  <Download className="h-4 w-4" /> Install App
                </button>
              ) : pwa.isIOS ? (
                <div className="border-t border-blue-200 bg-blue-100 p-3" data-testid="pwa-ios-guide">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-center gap-1">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm"><Share className="h-5 w-5 text-blue-600" /></div>
                      <span className="text-[9px] font-semibold text-blue-600">Step 1</span>
                    </div>
                    <div className="text-xs text-blue-800">
                      Tap the <span className="inline-flex items-center gap-0.5 rounded bg-white px-1.5 py-0.5 font-semibold shadow-sm"><Share className="h-3 w-3" /> Share</span> button at the bottom of Safari
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex flex-col items-center gap-1">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm"><span className="text-lg">+</span></div>
                      <span className="text-[9px] font-semibold text-blue-600">Step 2</span>
                    </div>
                    <div className="text-xs text-blue-800">
                      Scroll down and tap <span className="rounded bg-white px-1.5 py-0.5 font-semibold shadow-sm">Add to Home Screen</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-t border-blue-200 bg-blue-100 p-3" data-testid="pwa-android-guide">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm"><span className="text-lg font-bold text-slate-400">&#8942;</span></div>
                    <div className="text-xs text-blue-800">
                      Tap the <span className="rounded bg-white px-1.5 py-0.5 font-semibold shadow-sm">&#8942; Menu</span> at the top right, then <span className="rounded bg-white px-1.5 py-0.5 font-semibold shadow-sm">Add to Home Screen</span> or <span className="rounded bg-white px-1.5 py-0.5 font-semibold shadow-sm">Install app</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mode toggle */}
          <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1" data-testid="login-mode-toggle">
            {modes.map(m => (
              <button key={m.id} type="button" onClick={() => setMode(m.id)} className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${mode === m.id ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`login-mode-${m.id}`}>{m.label}</button>
            ))}
          </div>
          <p className="mt-3 min-h-[2.5rem] text-sm text-slate-500" data-testid="login-mode-hint">{activeMode?.hint}</p>

          <form onSubmit={loginMethod === 'code' ? handleVerifyCode : handleLogin} className="mt-4 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className="text-sm font-medium text-slate-700">Email</Label>
              <Input id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="h-11 rounded-xl border-slate-200 bg-slate-50 focus:bg-white" placeholder="you@company.com" data-testid="login-email-input" autoComplete="email" />
            </div>
            {loginMethod === 'password' ? (
              <div className="space-y-1.5">
                <Label htmlFor="login-password" className="text-sm font-medium text-slate-700">Password</Label>
                <Input id="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required className="h-11 rounded-xl border-slate-200 bg-slate-50 focus:bg-white" placeholder="••••••••" data-testid="login-password-input" autoComplete="current-password" />
              </div>
            ) : (
              <div className="space-y-1.5">
                {!codeSent ? (
                  <Button type="button" onClick={handleRequestCode} disabled={loading} variant="outline" className="h-11 w-full rounded-xl" data-testid="request-code-btn">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                    Send login code to email
                  </Button>
                ) : (
                  <>
                    <Label htmlFor="login-code" className="text-sm font-medium text-slate-700">6-digit code</Label>
                    <Input id="login-code" type="text" inputMode="numeric" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required className="h-11 rounded-xl border-slate-200 bg-slate-50 text-center text-2xl tracking-[0.5em] focus:bg-white" placeholder="000000" data-testid="login-code-input" autoComplete="one-time-code" maxLength={6} />
                    <p className="text-xs text-slate-400">Check your email for the 6-digit code. <button type="button" onClick={handleRequestCode} className="text-blue-600 underline">Resend</button></p>
                  </>
                )}
              </div>
            )}
            <Button type="submit" disabled={loading || (loginMethod === 'code' && !codeSent)} className="h-11 w-full rounded-xl bg-slate-950 text-white hover:bg-slate-800 disabled:opacity-60" data-testid="login-submit-button">
              {loading ? 'Signing in...' : 'Sign in'}{!loading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
            <button type="button" onClick={() => { setLoginMethod(m => m === 'password' ? 'code' : 'password'); setCodeSent(false); setCode(''); }} className="flex w-full items-center justify-center gap-2 text-sm text-slate-400 transition hover:text-slate-600" data-testid="toggle-login-method">
              {loginMethod === 'password' ? <><KeyRound className="h-3.5 w-3.5" /> Login with email code instead</> : <><KeyRound className="h-3.5 w-3.5" /> Login with password instead</>}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-5" data-testid="demo-credentials-card">
            <button type="button" onClick={() => setShowDemo(c => !c)} className="w-full rounded-xl border border-dashed border-slate-200 px-4 py-2.5 text-left text-sm text-slate-400 transition hover:border-slate-300 hover:text-slate-600" data-testid="demo-toggle-button">
              {showDemo ? 'Hide' : 'Show'} demo credentials
            </button>
            {showDemo && (
              <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-slate-50" data-testid="demo-credentials-list">
                {[
                  { label: 'Super admin', email: 'admin@coldchain.com', password: 'admin123', testId: 'demo-superadmin-credential' },
                  { label: 'Internal support', email: 'support@coldchain.com', password: 'support123', testId: 'demo-support-credential' },
                  { label: 'Tenant admin', email: 'admin@currypalace.com', password: 'demo123', testId: 'demo-tenant-credential' },
                ].map(item => (
                  <button key={item.email} type="button" onClick={() => fillDemo(item.email, item.password)} className="flex w-full items-center justify-between border-b border-slate-200 px-4 py-3 text-left transition last:border-0 hover:bg-white" data-testid={item.testId}>
                    <div><p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{item.label}</p><p className="mt-0.5 text-sm text-slate-700">{item.email}</p></div>
                    <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-300" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="mt-4 text-sm text-slate-400">Need an account? <Link to="/register" className="font-medium text-slate-700 underline underline-offset-2" data-testid="register-link">Register</Link></p>
        </div>
      </div>
    </div>
  );
};
